import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../utils/axiosInstance";
import ListImages from "./ListImages";
import { hasPermission } from "../../utils/permissionUtils";
import {
  Box,
  Typography,
  Alert,
  Paper,
  Divider,
  Button,
  CircularProgress,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

export default function AddImages() {
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState(null);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { chatbotId: paramsChatbotId } = useParams();

  // Permission states
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    document.title = "Upload Image | KlonIT Web Application";
  }, []);

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission("images", "view");
        const modifyPerm = await hasPermission("images", "add|edit");
        const deletePerm = await hasPermission("images", "delete");

        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
        setCanView(false);
        setCanModify(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  const handleFileChange = (event) => {
    setSelectedFiles(event.target.files);
  };

  const handleFileUpload = async () => {
    if (!selectedFiles) {
      setError("Please select file(s) to upload.");
      return;
    }
    setError("");
    setIsLoading(true);
    const formData = new FormData();
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      formData.append("files", file);
    }

    const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};

    try {
      const response = await axiosInstance.post(`/upload-image`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        params,
      });

      console.log("Upload successful:", response.data);
      setError(response.data.message);
      setSelectedFiles(null);
      const fileInput = document.getElementById("input-20");
      if (fileInput) fileInput.value = "";

      // Trigger refresh after successful upload
      setRefresh((prev) => !prev);
    } catch (error) {
      console.error("Error uploading files:", error);
      if (error.response) {
        setError(error.response.data.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking permissions
  if (checkingPermissions) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center"
        py={4}
        sx={{ margin: '0 auto', maxWidth: 400, minHeight: '50vh' }}
      >
        <CircularProgress size={30} />
        <Typography mt={2}>Checking permissions...</Typography>
      </Box>
    );
  }

  // Show access denied if no view permission
  if (!canView) {
    return (
      <Paper sx={{ p: 3, mt: 10, mx: "auto", maxWidth: "1200px" }}>
        <Alert severity="error">
          <Typography variant="h6" fontWeight={700}>
            Access Denied
          </Typography>
          <Typography>
            You don't have permission to view image data. Please contact your
            administrator.
          </Typography>
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper
        sx={{
          p: 3,
          mt: 10,
          mx: "auto",
          maxWidth: "1200px",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 3 }} color="textPrimary">
          Manage <strong>Images</strong>
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              margin: '0 auto',
              maxWidth: 600,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
              Upload Images
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'center' }}>
              Upload image files (.jpg, .jpeg, .png)
            </Typography>

            <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" justifyContent="center">
              <Button
                variant="outlined"
                component="label"
                disabled={isLoading}
                sx={{ minWidth: 200 }}
              >
                Choose Files
                <input
                  id="input-20"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  hidden
                />
              </Button>

              {selectedFiles && (
                <Typography variant="body2" color="textSecondary">
                  {selectedFiles.length} file(s) selected
                </Typography>
              )}

              {canModify && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFileUpload}
                  disabled={isLoading || !selectedFiles}
                  startIcon={
                    isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                >
                  {isLoading ? "Uploading..." : "Upload Files"}
                </Button>
              )}
            </Box>

            {error && (
              <Alert
                severity={
                  error.includes("success") || error.includes("successfully")
                    ? "success"
                    : "error"
                }
                sx={{ mt: 2, width: '100%' }}
                onClose={() => setError("")}
              >
                {error}
              </Alert>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box>
          <Typography variant="h6" sx={{ mb: 2 }} color="textPrimary">
            Uploaded Images
          </Typography>
          <ListImages
            refresh={refresh}
            chatbotId={paramsChatbotId}
            permission={{
              canView: canView,
              canModify: canModify,
              canDelete: canDelete,
            }}
          />
        </Box>
      </Paper>
    </>
  );
}